import { useState } from "react";
import { Copy, Check, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/data/status-badge";
import { useToast } from "@/hooks/use-toast";

export interface LinkAcesso {
  url: string;
  textoWhatsapp: string;
  expiraEm: string;
  emailEnviado: boolean;
}

/**
 * O link de convite/nova senha, pronto para COPIAR ou mandar por WhatsApp.
 *
 * É o mesmo dialog na tela Usuários (org) e na ficha da fazenda (Plataforma):
 * o admin manda o link, a pessoa escolhe a própria senha — a senha nunca passa
 * por WhatsApp. `textoWhatsapp` já vem BMP-only do servidor.
 */
export function LinkAcessoDialog({
  link, titulo, nome, telefone, onFechar,
}: {
  link: LinkAcesso | null;
  titulo: string;
  nome: string;
  /** Só dígitos com DDD; com ele o botão abre o wa.me da pessoa. */
  telefone?: string | null;
  onFechar: () => void;
}) {
  const { toast } = useToast();
  const [copiado, setCopiado] = useState(false);
  if (!link) return null;

  async function copiar() {
    try {
      await navigator.clipboard.writeText(link!.url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      toast({ title: "Não deu para copiar", description: "Selecione o link e copie manualmente.", variant: "destructive" });
    }
  }

  const digitos = (telefone ?? "").replace(/\D/g, "");
  const wa = `https://wa.me/${digitos ? (digitos.length <= 11 ? "55" + digitos : digitos) : ""}?text=${encodeURIComponent(link.textoWhatsapp)}`;
  const validade = new Date(link.expiraEm);

  return (
    <Dialog open onOpenChange={aberto => { if (!aberto) onFechar(); }}>
      <DialogContent data-testid="dialog-link-acesso">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription>
            Mande este link para {nome}. Vale até {isNaN(validade.getTime()) ? "—" : validade.toLocaleDateString("pt-BR")}; o anterior deixou de valer.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input readOnly value={link.url} onFocus={e => e.currentTarget.select()} className="font-mono text-xs" data-testid="input-link-acesso" />
            <Button variant="outline" size="icon" onClick={copiar} aria-label="Copiar link" data-testid="button-copiar-link">
              {copiado ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {link.emailEnviado
              ? <StatusBadge tom="sucesso" ponto>e-mail enviado</StatusBadge>
              : <StatusBadge tom="neutro">sem e-mail — mande o link</StatusBadge>}
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" asChild>
            <a href={wa} target="_blank" rel="noreferrer" data-testid="button-whatsapp-link">
              <MessageCircle className="h-4 w-4 mr-2" />WhatsApp
            </a>
          </Button>
          <Button onClick={onFechar} data-testid="button-fechar-link">Pronto</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
